import { can } from '../auth/permissions';

/**
 * What a workspace role lets you do, as the guest onboarding's "Your role"
 * stop reads it: `{ can: [lineKey…], cannot: [lineKey…] }`.
 *
 * Computed from `auth/permissions.js`, never written per role, so what the
 * screen promises cannot drift from what the API enforces. Each line is tied
 * to the actions that make it true, and holds when `can()` holds for all of
 * them. The copy lives in `onboarding:guest.role.can.<key>` and
 * `onboarding:guest.role.cannot.<key>`.
 *
 * Three rules shape the lists, so a role reads as a few plain sentences
 * rather than the matrix:
 *
 *  - **A line inside a broader one says less.** When a granted line's actions
 *    are all part of another granted line, only the broader one is listed
 *    ("add, edit and delete movements" already says "add movements"). On the
 *    other side it runs the other way: a denied line containing a narrower
 *    denied one is implied by it ("can't see statements" already says "can't
 *    upload them").
 *  - **Fillers only fill.** Every role has the dashboard and the assistant, so
 *    saying so tells a Co-Owner nothing; those lines are added only while the
 *    list is shorter than three, which in practice means a Viewer's.
 *  - **Cannot is short.** Denied lines are listed in a fixed priority, at most
 *    three, so a Viewer is not shown every admin action they lack.
 *
 * The snapshot test pins each role's lists, so a change to the matrix fails it
 * and makes someone reread the copy.
 */

// `permissions.js` does not mirror the backend's sensitive-field reads
// (`movement.read.note` / `.description`: Owner, Co-Owner and Member), and
// it is read-only for this feature. Editing a
// movement edits its note and description, so whoever may edit their own
// movements reads those fields: `movement.update.own` stands in, with exactly
// the same roles today. Swap it the day the mirror gains the real actions.
const READS_NOTES = 'movement.update.own';

// Order is the order of the "can" list.
export const LINES = [
  {
    key: 'seeEverything',
    actions: ['movement.read.standard', 'account.read', 'tag.read', 'goal.read', READS_NOTES],
  },
  { key: 'seeData', actions: ['movement.read.standard', 'account.read', 'tag.read', 'goal.read'] },
  {
    key: 'editAnyMovement',
    actions: [
      'movement.create', 'movement.update.own', 'movement.delete.own',
      'movement.update.any', 'movement.delete.any',
    ],
  },
  { key: 'addMovements', actions: ['movement.create', 'movement.update.own'] },
  {
    key: 'editStructure',
    actions: [
      'account.create', 'account.update', 'account.delete',
      'category.create', 'category.update', 'category.delete',
      'tag.create', 'tag.update', 'tag.delete',
      'goal.create', 'goal.update', 'goal.delete',
    ],
  },
  { key: 'buildStructure', actions: ['account.create', 'category.create', 'tag.create', 'goal.create'] },
  { key: 'deleteStructure', actions: ['account.delete', 'category.delete', 'tag.delete'] },
  // Uploading includes seeing what was uploaded (the Source files page).
  { key: 'uploadStatements', actions: ['statement.upload', 'source_file.read'] },
  { key: 'reviewStatements', actions: ['source_file.read'] },
  { key: 'manageMembers', actions: ['member.read', 'member.invite', 'member.remove'] },
  { key: 'deleteSpace', actions: ['workspace.delete', 'workspace.transfer_ownership'] },
  { key: 'seeDashboard', actions: ['movement.read.summary'], filler: true },
  { key: 'askAssistant', actions: ['workspace.read'], filler: true },
];

// Which denied lines come first under "cannot". A line absent here is never
// listed as a "cannot": nobody lacks the fillers.
//
// addMovements reads "create or edit anything" when denied: a role without
// `movement.create` creates nothing at all in today's matrix. If that stops
// being true, the snapshot test is what fails.
export const CANNOT_PRIORITY = [
  'addMovements',
  'uploadStatements',
  'reviewStatements',
  'seeEverything',
  'editAnyMovement',
  'deleteStructure',
  'manageMembers',
  'deleteSpace',
  'buildStructure',
  'editStructure',
  'seeData',
];

const MIN_CAN = 3;
const MAX_CANNOT = 3;

const holds = (role, line) => line.actions.every((a) => can(role, a));
// Every action of `inner` is one of `outer`'s, and `outer` has more.
const within = (inner, outer) => inner !== outer
  && inner.actions.length < outer.actions.length
  && inner.actions.every((a) => outer.actions.includes(a));

export function roleSummary(role) {
  const lines = LINES.filter((l) => !l.filler);
  const granted = lines.filter((l) => holds(role, l));
  const denied = lines.filter((l) => !holds(role, l));

  const canLines = granted.filter((l) => !granted.some((o) => within(l, o)));
  LINES.filter((l) => l.filler && holds(role, l)).forEach((l) => {
    if (canLines.length < MIN_CAN) canLines.push(l);
  });

  const deniedShown = denied.filter((l) => !denied.some((o) => within(o, l)));
  const cannot = CANNOT_PRIORITY
    .map((key) => deniedShown.find((l) => l.key === key))
    .filter(Boolean)
    .slice(0, MAX_CANNOT)
    .map((l) => l.key);

  return { can: canLines.map((l) => l.key), cannot };
}
